import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// project import
import Drawer from './Drawer';
import Header from './Header';
import AppFooter from './AppFooter';
import { collapsedWidth } from './Drawer/MiniDrawerStyled';
import { LAYOUT } from '@/components/cpv-ui/tokens';
import { NotificationSocketProvider } from '@/contexts/NotificationSocketContext';
// PWPOL lane (system-admin F5): "your password expires in N days" banner. The forced
// change-password modal itself is mounted at the App root so it also covers the login bootstrap.
import { PasswordExpiryBanner } from '@/components/PasswordChangeGate';
// The header's return icon and the page-action rail read/write this per-page state.
import { PageChromeProvider, usePageChrome } from '@/components/PageChrome';
import { useScope } from '@/contexts/ScopeContext';

// types
import { openDrawer } from '@/store/reducers/menu';

// ==============================|| MAIN LAYOUT ||============================== //

// The scrolling content area. Keeps a strip clear on the right while a page shows its rail.
const ContentArea = () => {
  const { railCount } = usePageChrome();
  // CPV-MT-01 F1: a new location choice re-opens the page, so every screen reads the plants now in view
  const { version: scopeVersion, settled: scopeSettled } = useScope();
  const areaRef = useRef(null);
  const bannerRef = useRef(null);
  // The password-expiry banner sits above the page and takes real height; page cards are sized from the viewport, so its
  // height is published as --shell-banner and index.css takes it off their height (otherwise the whole page scrolls).
  useEffect(() => {
    const area = areaRef.current;
    const banner = bannerRef.current;
    if (!area || !banner || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => area.style.setProperty('--shell-banner', `${banner.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(banner);
    return () => ro.disconnect();
  }, []);
  return (
    // paddingBottom = the fixed footer's height (LAYOUT.appFooter = 26px: one 16px text line + py-1 + two 1px borders), so the last card ends
    // right above it. Page cards are calc(100dvh - 120px) = 64px top bar + 20px/10px page padding + this 26px.
    <div ref={areaRef} className={`app-content-scroll flex-1 overflow-auto ${railCount > 0 ? 'page-rail-gutter' : ''}`} style={{ paddingBottom: LAYOUT.appFooter }}>
      <div ref={bannerRef}>
        <PasswordExpiryBanner />
      </div>
      {scopeSettled ? <Outlet key={scopeVersion} /> : null}
    </div>
  );
};

const MainLayout = () => {
  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(drawerOpen);
  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
  }, [drawerOpen]);

  // Main layout always stays pinned at the collapsed rail width — the sidebar
  // overlays on top when opened instead of pushing/resizing the page.
  const contentMarginLeft = collapsedWidth;

  return (
    <NotificationSocketProvider>
      <PageChromeProvider>
      <div className="flex w-full min-h-screen">
        <Header open={open} handleDrawerToggle={handleDrawerToggle} />
        <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />

        {/* Backdrop — dims the header + page while the sidebar is expanded;
            clicking it closes the sidebar without navigating. */}
        <div
          onClick={handleDrawerToggle}
          className="fixed inset-0 transition-opacity duration-[400ms]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 1250,
            opacity: open ? 1 : 0,
            visibility: open ? 'visible' : 'hidden',
            pointerEvents: open ? 'auto' : 'none',
          }}
        />

        {/* Main Content Area */}
        <div
          className="flex flex-col min-h-screen transition-all duration-300"
          style={{
            marginLeft: contentMarginLeft,
            width: `calc(100% - ${contentMarginLeft}px)`,
          }}
        >
          {/* Toolbar spacer */}
          <div className="h-16 flex-shrink-0" />

          {/* Content Area - add padding bottom for fixed footer */}
          <ContentArea />
        </div>

        {/* Fixed Footer at bottom */}
        <AppFooter left={contentMarginLeft} />
      </div>
      </PageChromeProvider>
    </NotificationSocketProvider>
  );
};

export default MainLayout;
